import createRequest from '../../api/httpRequest';
import { all, put, takeEvery, select } from 'redux-saga/effects';
import API from '../../api/message';
import API_COMPANY from '../../api/company';
import NotificationTypes from '../notification/NotificationTypes';
import MessageTypes from './MessageTypes';
import snakecaseKeys from 'snakecase-keys';
import Util from '../../utils/Util';
import CompanyTypes from '../company/CompanyTypes';
import { getCompanyDomain } from '../company/companySelector';
import { getAuthUser } from '../session/sessionSelector';
import DataTypes from '../data/DataTypes';

function * updateTermOfUsage () {
  yield takeEvery(MessageTypes.UPDATE_TERM_OF_USAGE_REQUEST, _updateTermOfUsage);
}

function * _updateTermOfUsage ({ payload }) {
  const request = snakecaseKeys({ ...payload });
  const user = yield select(getAuthUser);
  yield createRequest({
    api: {
      ...API_COMPANY.UPDATE_COMPANY_TERM,
      data: request
    },
    onSuccess: function * (response) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.update.${response.status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      yield put({
        type: CompanyTypes.GET_COMPANY_DETAIL_REQUEST,
        payload: user.companyId
      });
    },
    onError: function * ({ status }) {
      yield put({
        type: MessageTypes.UPDATE_TERM_OF_USAGE_ERROR,
        payload: status
      });
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.error.${status}`,
            level: 'error',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
    }
  });
}

function * loadSectionTitleDescription () {
  yield takeEvery(MessageTypes.GET_SECTION_TITLE_DESCRIPTION_REQUEST, _loadSectionTitleDescription);
}

function * _loadSectionTitleDescription ({ payload }) {
  const domain = yield select(getCompanyDomain);
  const request = snakecaseKeys({ ...payload, domain: domain });
  yield createRequest({
    api: {
      ...API.GET_SECTION_DETAIL_LIST,
      params: request
    },
    onSuccess: function * (response) {
      yield put({
        type: MessageTypes.GET_SECTION_TITLE_DESCRIPTION_SUCCESS,
        payload: {
          content: Util.toCamelCaseKey(response.data),
          type: payload.sectionType
        }
      });
    }
  });
}

function * loadTableHeading () {
  yield takeEvery(MessageTypes.GET_TABLE_HEADING_REQUEST, _loadTableHeading);
}

function * _loadTableHeading ({ payload }) {
  const domain = yield select(getCompanyDomain);
  const request = snakecaseKeys({ ...payload, domain: domain });
  yield createRequest({
    api: {
      ...API.GET_TABLE_HEADING_LIST,
      params: request
    },
    onSuccess: function * (response) {
      yield put({
        type: MessageTypes.GET_TABLE_HEADING_SUCCESS,
        payload: {
          content: Util.toCamelCaseKey(response.data),
          type: payload.sectionType
        }
      });
    }
  });
}

function * updateTitleDescription () {
  yield takeEvery(MessageTypes.UPDATE_TITLE_DESCRIPTION_REQUEST, _updateTitleDescription);
}

function * _updateTitleDescription ({ payload }) {
  const domain = yield select(getCompanyDomain);
  const request = snakecaseKeys(payload.data);
  yield createRequest({
    api: {
      ...API.UPDATE_SECTION_DETAIL,
      data: request
    },
    onSuccess: function * ({ status }) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.update.${status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      yield put({
        type: MessageTypes.GET_SECTION_TITLE_DESCRIPTION_REQUEST,
        payload: {
          sectionType: payload.type,
          domain: domain
        }
      });
    }
  });
}

function * updateTableHeading () {
  yield takeEvery(MessageTypes.UPDATE_TABLE_HEADING_REQUEST, _updateTableHeading);
}

function * _updateTableHeading ({ payload }) {
  const domain = yield select(getCompanyDomain);
  yield createRequest({
    api: {
      ...API.UPDATE_TABLE_HEADING,
      data: payload.data
    },
    onSuccess: function * ({ status }) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.update.${status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      yield put({
        type: MessageTypes.GET_TABLE_HEADING_REQUEST,
        payload: {
          sectionType: payload.type,
          domain: domain
        }
      });
    }
  });
}

function * updatePageTitle () {
  yield takeEvery(MessageTypes.UPDATE_PAGE_TITLE_REQUEST, _updatePageTitle);
}

function * _updatePageTitle ({ payload }) {
  const request = snakecaseKeys(payload);
  yield createRequest({
    api: {
      ...API_COMPANY.UPDATE_PAGE_TITLE,
      data: request
    },
    onSuccess: function * ({ status }) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.update.${status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      yield put({
        type: CompanyTypes.GET_COMPANY_DETAIL_REQUEST,
        payload: payload.id
      });
    }
  });
}

function * getTabPageTitle () {
  yield takeEvery(MessageTypes.GET_TAB_PAGE_TITLE_REQUEST, _getTabPageTitle);
}

function * _getTabPageTitle ({ payload }) {
  const domain = yield select(getCompanyDomain);
  yield createRequest({
    api: {
      ...API.GET_TAB_PAGE_TITLE,
      params: {
        domain,
        is_preview: payload ? payload.isPreview : true
      }
    },
    onSuccess: function * ({ data }) {
      yield put({
        type: MessageTypes.GET_TAB_PAGE_TITLE_SUCCESS,
        payload: Util.toCamelCaseKey(data)
      });
    }
  });
}

function * updateTabPageTitle () {
  yield takeEvery(MessageTypes.UPDATE_TAB_PAGE_TITLE_REQUEST, _updateTabPageTitle);
}

function * _updateTabPageTitle ({ payload }) {
  const request = {
    disclosure_top_page: payload.disclosureTopPage,
    faq_top_page: payload.faqTopPage,
    patient_top_page: payload.patientTopPage,
    search_result_top_page: payload.searchResultTopPage
  };
  yield createRequest({
    api: {
      ...API.UPDATE_TAB_PAGE_TITLE,
      data: request
    },
    onSuccess: function * ({ status }) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.update.${status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      yield put({
        type: MessageTypes.GET_TAB_PAGE_TITLE_REQUEST
      });
    }
  });
}

function * updateAllTableHeadings () {
  yield takeEvery(MessageTypes.UPDATE_ALL_TABLE_HEADINGS, _updateAllTableHeadings);
}

function * _updateAllTableHeadings ({ payload }) {
  yield createRequest({
    api: {
      ...API.UPDATE_ALL_TABLE_HEADINGS
    },
    onSuccess: function * ({ status }) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.update.${status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
    }
  });
}

function * updateAllSectionDetails () {
  yield takeEvery(MessageTypes.UPDATE_ALL_SECTION_DETAILS, _updateAllSectionDetails);
}

function * _updateAllSectionDetails ({ payload }) {
  yield createRequest({
    api: {
      ...API.UPDATE_ALL_SECTION_DETAILS
    },
    onSuccess: function * ({ status }) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.update.${status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
    }
  });
}

function * indexAllDataToElasticSearch () {
  yield takeEvery(MessageTypes.INDEX_ALL_DATA_TO_ELASTICSEARCH, _indexAllDataToElasticSearch);
}

function * _indexAllDataToElasticSearch ({ payload }) {
  yield put({
    type: DataTypes.UPDATE_LOADING,
    payload: { isLoading: true, companyId: payload.company_id, type: 'indexData' }
  });
  yield createRequest({
    api: {
      ...API.INDEX_ALL_DATA_TO_ELASTICSEARCH,
      params: payload
    },
    onSuccess: function * ({ status }) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.update.${status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      yield put({
        type: DataTypes.UPDATE_LOADING,
        payload: null
      });
    }
  });
}

function * updateTermPreview () {
  yield takeEvery(MessageTypes.UPDATE_TERM_PREVIEW, function * () {
    yield createRequest({
      api: {
        ...API.UPDATE_TERM_PREVIEW
      },
      onSuccess: function * ({ status }) {
        yield put({
          type: NotificationTypes.SHOW_NOTIFICATION,
          payload: {
            config: {
              message: `api.update.${status}`,
              level: 'success',
              autoDismiss: 3,
              position: 'tc'
            }
          }
        });
      }
    });
  });
}

function * updatePageTitlePreview () {
  yield takeEvery(MessageTypes.UPDATE_PAGE_TITLE_PREVIEW, function * () {
    yield createRequest({
      api: {
        ...API.UPDATE_PAGE_TITLE_PREVIEW
      },
      onSuccess: function * ({ status }) {
        yield put({
          type: NotificationTypes.SHOW_NOTIFICATION,
          payload: {
            config: {
              message: `api.update.${status}`,
              level: 'success',
              autoDismiss: 3,
              position: 'tc'
            }
          }
        });
      }
    });
  });
}

function * updateTabTitlePreview () {
  yield takeEvery(MessageTypes.UPDATE_TAB_TITLE_PREVIEW, function * () {
    yield createRequest({
      api: {
        ...API.UPDATE_TAB_TITLE_PREVIEW
      },
      onSuccess: function * ({ status }) {
        yield put({
          type: NotificationTypes.SHOW_NOTIFICATION,
          payload: {
            config: {
              message: `api.update.${status}`,
              level: 'success',
              autoDismiss: 3,
              position: 'tc'
            }
          }
        });
      }
    });
  });
}

function * updateHeadingTablePreview () {
  yield takeEvery(MessageTypes.UPDATE_HEADING_TABLE_PREVIEW, function * () {
    yield createRequest({
      api: {
        ...API.UPDATE_HEADING_TABLE_PREVIEW
      },
      onSuccess: function * ({ status }) {
        yield put({
          type: NotificationTypes.SHOW_NOTIFICATION,
          payload: {
            config: {
              message: `api.update.${status}`,
              level: 'success',
              autoDismiss: 3,
              position: 'tc'
            }
          }
        });
      }
    });
  });
}

function * updateSectionDetailPreview () {
  yield takeEvery(MessageTypes.UPDATE_SECTION_DETAIL_PREVIEW, function * () {
    yield createRequest({
      api: {
        ...API.UPDATE_SECTION_DETAIL_PREVIEW
      },
      onSuccess: function * ({ status }) {
        yield put({
          type: NotificationTypes.SHOW_NOTIFICATION,
          payload: {
            config: {
              message: `api.update.${status}`,
              level: 'success',
              autoDismiss: 3,
              position: 'tc'
            }
          }
        });
      }
    });
  });
}

function * updateAllPreview () {
  yield takeEvery(MessageTypes.UPDATE_ALL_PREVIEW, _updateAllPreview);
}

function * _updateAllPreview ({ payload }) {
  yield createRequest({
    api: {
      ...API.UPDATE_ALL_PREVIEW
    },
    onSuccess: function * ({ status }) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.update.${status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
    }
  });
}

export default function * messageSaga () {
  yield all([
    updateTermOfUsage(),
    loadSectionTitleDescription(),
    loadTableHeading(),
    updateTitleDescription(),
    updateTableHeading(),
    updatePageTitle(),
    getTabPageTitle(),
    updateTabPageTitle(),
    updateAllTableHeadings(),
    updateAllSectionDetails(),
    indexAllDataToElasticSearch(),
    updateTermPreview(),
    updatePageTitlePreview(),
    updateTabTitlePreview(),
    updateHeadingTablePreview(),
    updateSectionDetailPreview(),
    updateAllPreview()
  ]);
}
